import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import ConsolidateBillingContainer from './ConsolidateBilling/ConsolidateBilling.container';
import DaysBeforeRenewalContainer from './DaysBeforeRenewal/DaysBeforeRenewal.container';
import DirectDebitContainer from './DirectDebit/DirectDebit.container';
import HideContractLinesInInvoiceContainer from './HideContractLinesInInvoice/HideContractLinesInInvoice.container';
import PauseBillingContainer from './PauseBilling/PauseBilling.container';
import ProformaInvoicingContainer from './ProformaInvoicing/ProformaInvoicing.container';
import ResellerContainer from './Reseller/Reseller.container';
import BillingIntervalContainer from './BillingInterval/BillingInterval.container';
import PanelIconTitle from '@/components/common/PanelIconTitle/PanelIconTitle';
import ActiveStatusContainer from '../Statuses/components/ActiveStatus/ActiveStatus.container';
import CancelledStatusContainer from '../Statuses/components/CancelledStatus/CancelledStatus.container';
import DnrStatusContainer from '../Statuses/components/DnrStatus/DnrStatus.container';
import TerminateStatusContainer from '../Statuses/components/TerminateStatus/TerminateStatus.container';
import styles from './billing-container.module.scss';
import { EnvUtils } from '@/utils/env.utils';
import EditContractHeaderContainer from './EditContractHeader/EditContractHeaderContainer.container';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { UserRole } from '@/config/roles.config';
import ViewUpliftsHistoryContainer from './ViewUpliftsHistory/ViewUpliftsHistory.container';
import BillingCadenceContainer from './BillingCadence/BillingCadence.container';
import BulkUpdateContractLineCustomers from
  './BulkUpdateContractLineCustomers/BulkUpdateContractLineCustomers.container';
import AssignPurchaseOrder from './AssignPurchaseOrderForLines/AssignPurchaseOrder.container';
import UpdateCustomerContainer from './UpdateCustomer/UpdateCustomerContainer';
import ManageBundlesContainer from './ManageBundles/ManageBundles.container';
import UnassignPurchaseOrder from './UnassignPurchaseOrderForLines/UnassignPurchaseOrder.container';
import BulkUpdateContractLineProRataEndDateContainer from
  './BulkUpdateContractLineProRataEndDate/BulkUpdateContractLineProRataEndDate.container';
import ContractIqVerifyContainer from './ContractIqVerify/ContractIqVerify.container';
import {
  useSelectedLines
} from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import {
  useSelectedVariableLines
} from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/variableLinesSelectionObservable';
import {
  ContractStatusEnum,
  ContractLineBillingCycleOutput,
  VariableContractLineOutput
} from '@/graphql/contracts/_generated_/contracts';
import useGetContractDetailsFromCache from '@/hooks/contract/useGetContractDetailsFromCache.hook';
import { isContractOneEvoMigrated } from '@/utils/oneEvoMigration.utils';

enum RightPanelSections {
  Billing = 'Billing',
  Status = 'Status',
  ContractSetup = 'Contract Setup',
  Misc = 'Miscellaneous',
  FullAccess = 'Full Access'
}

export default function ActionsContainer() {
  const { contractId }: { contractId: UUID } = useParams();
  const isOneEvoMigrated = isContractOneEvoMigrated(useGetContractDetailsFromCache(contractId));
  const { getUserRole } = useGetUserRole();
  const [userRole, setUserRole] = useState<UserRole>();
  const selectedLines = useSelectedLines();
  const selectedVariableLines = useSelectedVariableLines();
  const fullAccessRole = userRole == UserRole.FULL_ACCESS;
  const fullAccessOrAdminRole = fullAccessRole || userRole == UserRole.ADMIN;
  const fullAccessOrAdminOrUserRole = fullAccessOrAdminRole || userRole == UserRole.USER;
  const userRoleOnly = userRole == UserRole.USER;
  const readerRole = userRole == UserRole.READER;

  const linesFromBothGridsSelected = selectedLines.length && selectedVariableLines.length;
  const hasRegularLines = selectedLines.length > 0;
  const hasVariableLines = selectedVariableLines.length > 0;
  const hideDNRForVariableOnProdAndStaging = selectedVariableLines.length > 0 &&
    (EnvUtils.azureEnvIs('production') || EnvUtils.azureEnvIs('staging'));

  const shouldShowStatusContainers = useCallback((correctStatuses: ContractStatusEnum[]) => {
    if (selectedLines.length > 0) {
      const statuses = selectedLines.map((slr) => {
        const contractLine = slr.payload.contractLine as ContractLineBillingCycleOutput;
        return contractLine.status!;
      });
      return statuses.every(status => correctStatuses.includes(status));
    }

    if (selectedVariableLines.length > 0) {
      const statusesVB = selectedVariableLines
        .map((slr) => {
          const contractLine = slr.payload.contractLine as VariableContractLineOutput;
          return contractLine.status;
        })
        .filter((status): status is ContractStatusEnum => status !== undefined && status !== null);
      return statusesVB.length === selectedVariableLines.length &&
        statusesVB.every(status => correctStatuses?.includes(status));
    }
    return false;

  }, [selectedLines, selectedVariableLines]);


  const hasLinesSelected = selectedLines.length > 0 || selectedVariableLines.length > 0;

  useEffect(() => {
    const getRole = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };

    getRole();
  }, [getUserRole]);

  const getSectionWrapper = (section: RightPanelSections, content: JSX.Element) =>
    (<>
    <div className={styles.sectionHeader}>
      <label>{section}</label>
    </div>
    <div className={styles.actionsColumn}>
      <div className={styles.actionsRow}>
        {content}
      </div>
    </div>
  </>);


  const shouldShowActiveStatus = hasLinesSelected && !linesFromBothGridsSelected &&
    shouldShowStatusContainers([
      ContractStatusEnum.ActiveDNR,
      ContractStatusEnum.AwaitingApproval,
      ContractStatusEnum.Cancelled,
      ContractStatusEnum.Expired,
      ContractStatusEnum.Inactive,
      ContractStatusEnum.Terminated,
      ContractStatusEnum.Terminating
    ]);
  const shouldShowCancelledStatus = hasLinesSelected && !linesFromBothGridsSelected &&
    shouldShowStatusContainers([
      ContractStatusEnum.Active,
      ContractStatusEnum.Inactive
    ]);
  const shouldShowDnrStatus = hasLinesSelected && !linesFromBothGridsSelected && !hideDNRForVariableOnProdAndStaging &&
    shouldShowStatusContainers([
      ContractStatusEnum.Active,
      ContractStatusEnum.ActiveDNR,
      ContractStatusEnum.Terminating
    ]);
  const shouldShowTerminateStatus = hasRegularLines && !hasVariableLines &&
    shouldShowStatusContainers([
      ContractStatusEnum.Active,
      ContractStatusEnum.ActiveDNR,
      ContractStatusEnum.Terminating
    ]);
  const notReaderAndOneEvoEnabled = !readerRole;
  const shouldBulkUpdateLineParties = fullAccessOrAdminRole;
  const shouldProRataFirstPeriod = fullAccessOrAdminRole &&
    EnvUtils.isProRataFirstPeriodEnabled();

  const isOperatingOnVariableLines = !hasRegularLines && hasVariableLines;
  const activeSelectedLines = isOperatingOnVariableLines ? selectedVariableLines : selectedLines;

  const getContractLineStatus = (contractLine: any): ContractStatusEnum | undefined => {
    return ((contractLine as ContractLineBillingCycleOutput)?.status ||
      (contractLine as VariableContractLineOutput)?.status) as
      | ContractStatusEnum
      | undefined;
  };

  const hasUserAllowedStatuses = useMemo(() => {
    const allowedStatuses = [
      ContractStatusEnum.Active,
      ContractStatusEnum.ActiveDNR,
      ContractStatusEnum.Terminating
    ];

    return activeSelectedLines.every(line => {
      const status = getContractLineStatus(line.payload.contractLine);
      return status && allowedStatuses.includes(status);
    });
  }, [activeSelectedLines]);

  const userRoleAllowed = userRoleOnly ? (hasLinesSelected && hasUserAllowedStatuses) : true;

  const hasExcludedStatuses = useMemo(() => {
    const excludedStatuses = [
      ContractStatusEnum.Cancelled,
      ContractStatusEnum.Expired,
      ContractStatusEnum.Terminated,
      ContractStatusEnum.Inactive
    ];

    return activeSelectedLines.some(line => {
      const status = getContractLineStatus(line.payload.contractLine);
      return status && excludedStatuses.includes(status);
    });
  }, [activeSelectedLines]);

  const enableLinePause = activeSelectedLines.length > 0 && !hasExcludedStatuses;
  const showLinePauseReadOnly = activeSelectedLines.length > 0 && hasExcludedStatuses;

  const shouldShowBillingCadence = (fullAccessOrAdminOrUserRole || readerRole) && hasLinesSelected;

  const shouldShowBillingSection = enableLinePause || showLinePauseReadOnly || shouldShowBillingCadence;
  const shouldShowStatusSection = !isOneEvoMigrated && (shouldShowActiveStatus ||
    shouldShowDnrStatus ||
    shouldShowCancelledStatus ||
    shouldShowTerminateStatus);

  return (
    <>
      <div className="mb-20 mt-20 ml-5">
        <PanelIconTitle>Actions</PanelIconTitle>
      </div>

      {shouldShowBillingSection &&
        getSectionWrapper(RightPanelSections.Billing,
          <>
            {shouldShowBillingCadence && <BillingCadenceContainer />}
            <PauseBillingContainer
              disabled={showLinePauseReadOnly}
              readOnly={!fullAccessOrAdminRole || isOneEvoMigrated}
            />
          </>)}

      {shouldShowStatusSection && getSectionWrapper(RightPanelSections.Status,
        <>
          {shouldShowActiveStatus && fullAccessOrAdminRole && (<ActiveStatusContainer />)}
          {shouldShowDnrStatus && (<DnrStatusContainer readOnly={!fullAccessOrAdminRole} />)}
          {shouldShowCancelledStatus && fullAccessOrAdminRole && (<CancelledStatusContainer />)}
          {shouldShowTerminateStatus && (<TerminateStatusContainer readOnly={!fullAccessOrAdminRole} />)}
        </>)}

      {fullAccessOrAdminOrUserRole && !isOneEvoMigrated && getSectionWrapper(RightPanelSections.ContractSetup, <>
        {shouldBulkUpdateLineParties && <BulkUpdateContractLineCustomers />}
        {fullAccessOrAdminRole && notReaderAndOneEvoEnabled && <ManageBundlesContainer />}
        {fullAccessOrAdminRole && <DaysBeforeRenewalContainer />}
        <ResellerContainer />
        <HideContractLinesInInvoiceContainer />
        <DirectDebitContainer />
        {notReaderAndOneEvoEnabled && userRoleAllowed && <AssignPurchaseOrder />}
        {notReaderAndOneEvoEnabled && userRoleAllowed && <UnassignPurchaseOrder />}
        {fullAccessOrAdminRole && <ConsolidateBillingContainer />}
        {fullAccessOrAdminRole && <BillingIntervalContainer />}
        {shouldProRataFirstPeriod &&
          <BulkUpdateContractLineProRataEndDateContainer />}
      </>)}

      {getSectionWrapper(RightPanelSections.Misc, <>
        <ViewUpliftsHistoryContainer />
        {fullAccessOrAdminOrUserRole && <ProformaInvoicingContainer />}
        {fullAccessOrAdminOrUserRole && !isOneEvoMigrated && <ContractIqVerifyContainer />}
      </>)}

      {fullAccessRole && !isOneEvoMigrated && getSectionWrapper(RightPanelSections.FullAccess, <>
        {fullAccessRole && <EditContractHeaderContainer />}
        {fullAccessRole && <UpdateCustomerContainer />}
      </>)}

    </>
  );
}
